import type { ReactNode } from "react";

import { cardStyles } from "../../core/Card.styles.js";
import { Stepper } from "../../core/Stepper.js";
import { cn } from "../../foundations/cn.js";
import { Amount } from "../Amount.js";
import { BoxArt } from "../BoxArt.js";

export interface BattleBoxPickProps {
  name: string;
  imageUrl: string;
  /** The box's price, in cents: what one round of it adds to the entry. */
  priceCents: number;
  /** Rounds of this box in the battle. */
  quantity: number;
  /** The stepper's name, read with the count: "Rounds of Tide Pool". */
  quantityLabel: string;
  /** The most rounds of it the battle can take now; + waits there. */
  maxQuantity: number;
  /** Called with the next count from − and +, the arrow keys or a typed number. */
  onQuantityChange: (quantity: number) => void;
  /** The − button's name: "One fewer round of Tide Pool". */
  decreaseLabel: string;
  /** The + button's name: "One more round of Tide Pool". */
  increaseLabel: string;
  /** Where the box opens in the battle ("1."), for a picked box. */
  position?: string;
  /** More controls under the stepper: move earlier or later, remove. */
  actions?: ReactNode;
  className?: string;
}

/**
 * A box in a battle being built: its wrapper, price and how many rounds of it, stepped with − and
 * + (a Stepper). Picked (a quantity above zero), it takes the accent edge.
 */
export function BattleBoxPick({
  name,
  imageUrl,
  priceCents,
  quantity,
  quantityLabel,
  maxQuantity,
  onQuantityChange,
  decreaseLabel,
  increaseLabel,
  position,
  actions,
  className,
}: BattleBoxPickProps) {
  return (
    <div
      className={cardStyles({
        radius: "xl",
        fill: "raised",
        // Picked, the accent edge alone: its fill stays raised, so the box art keeps its contrast.
        edge: "none",
        className: cn(
          "flex min-w-0 flex-col gap-2 border p-3",
          quantity > 0 ? "border-accent-default" : "border-border-subtle",
          className,
        ),
      })}
    >
      <div className="relative flex aspect-[4/5] items-center justify-center rounded-lg bg-bg-inset p-2">
        <BoxArt src={imageUrl} alt="" />
        {position && (
          <span className="absolute top-1.5 left-1.5 rounded-pill bg-bg-surface px-1.5 font-mono text-mono-meta text-text-secondary">
            {position}
          </span>
        )}
      </div>
      <div className="flex min-w-0 flex-col">
        <span className="truncate font-body text-body-b2-strong text-text-primary">{name}</span>
        <Amount cents={priceCents} size="meta" className="text-text-secondary" />
      </div>
      <Stepper
        label={quantityLabel}
        decrementLabel={decreaseLabel}
        incrementLabel={increaseLabel}
        value={quantity}
        min={0}
        max={maxQuantity}
        onValueChange={onQuantityChange}
      />
      {actions && <div className="flex items-center justify-center gap-1">{actions}</div>}
    </div>
  );
}
